type VoiceState = "uninitialized" | "initializing" | "ready" | "error";

interface VoiceControllerState {
  state: VoiceState;
  error: string | null;
}

class VoiceController {
  private static instance: VoiceController | null = null;
  
  private state: VoiceState = "uninitialized";
  private error: string | null = null;
  private queue: Array<{ text: string; onEnd?: () => void }> = [];
  private isProcessing = false;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private voicesLoaded = false;
  private selectedVoice: SpeechSynthesisVoice | null = null;
  private speechRate: number = 1.0;
  
  private constructor() {
    try {
      const saved = typeof localStorage !== "undefined" ? localStorage.getItem("memnora-narration-speed") : null;
      if (saved) {
        const parsed = parseFloat(saved);
        if (!isNaN(parsed) && parsed >= 0.5 && parsed <= 2.0) {
          this.speechRate = parsed;
        }
      }
    } catch (_) {}
  }
  
  static getInstance(): VoiceController {
    if (!VoiceController.instance) {
      VoiceController.instance = new VoiceController();
    }
    return VoiceController.instance;
  }
  
  getState(): VoiceControllerState {
    return { state: this.state, error: this.error };
  }
  
  isReady(): boolean {
    return this.state === "ready";
  }
  
  isSupported(): boolean {
    return typeof window !== "undefined" && "speechSynthesis" in window;
  }
  
  async initialize(): Promise<boolean> {
    if (this.state === "ready") return true;
    if (this.state === "initializing") return false;
    
    if (!this.isSupported()) {
      this.state = "error";
      this.error = "Speech synthesis not supported";
      return false;
    }
    
    this.state = "initializing";
    
    try {
      window.speechSynthesis.getVoices();
      
      await new Promise<void>((resolve) => {
        const checkVoices = () => {
          const voices = window.speechSynthesis.getVoices();
          if (voices.length > 0) {
            this.voicesLoaded = true;
            resolve();
          }
        };
        
        checkVoices();
        if (!this.voicesLoaded) {
          window.speechSynthesis.addEventListener("voiceschanged", checkVoices, { once: true });
          setTimeout(resolve, 2000);
        }
      });
      
      this.selectedVoice = this.getPreferredVoice();
      if (this.selectedVoice) {
        console.log(`[VoiceController] Selected voice: ${this.selectedVoice.name} (${this.selectedVoice.lang})`);
      }
      
      const unlock = new SpeechSynthesisUtterance("");
      unlock.volume = 0;
      window.speechSynthesis.speak(unlock);
      window.speechSynthesis.cancel();
      
      this.state = "ready";
      this.error = null;
      console.log("[VoiceController] Initialized successfully");
      return true;
    } catch (err) {
      this.state = "error";
      this.error = err instanceof Error ? err.message : "Unknown error";
      console.error("[VoiceController] Initialization failed:", err);
      return false;
    }
  }
  
  private getPreferredVoice(): SpeechSynthesisVoice | null {
    if (!this.isSupported()) return null;
    
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return null;

    const preferredNames = [
      "Samantha",
      "Google UK English Female",
      "Google US English",
      "Microsoft Zira",
      "Karen",
      "Victoria",
      "Moira",
      "Fiona",
      "Tessa",
      "Veena",
      "Microsoft Aria",
      "en-US-Wavenet-F",
      "en-US-Wavenet-C",
      "en-GB-Wavenet-A",
    ];

    for (const name of preferredNames) {
      const match = voices.find(v => v.name.includes(name));
      if (match) return match;
    }

    const femaleVoice = voices.find(v =>
      v.lang.startsWith("en") && v.name.toLowerCase().includes("female")
    );
    if (femaleVoice) return femaleVoice;

    const englishVoice = voices.find(v => v.lang.startsWith("en"));
    if (englishVoice) return englishVoice;

    return voices[0] || null;
  }
  
  private cleanText(text: string): string {
    return text
      .replace(/[+\-]\d/g, m => m === "+1" ? "plus one" : m === "-1" ? "minus one" : m)
      .replace(/π²/g, "pi squared")
      .replace(/τ/g, "tau")
      .replace(/[*_#`~>\[\]]/g, "")
      .replace(/\(([^)]+)\)/g, ", $1,")
      .replace(/https?:\/\/\S+/g, "")
      .replace(/\n{2,}/g, ". ")
      .replace(/\n/g, ". ")
      .replace(/\.{2,}/g, ".")
      .replace(/\s{2,}/g, " ")
      .trim();
  }
  
  private processQueue(): void {
    if (this.isProcessing || this.queue.length === 0) return;
    if (!this.isSupported() || this.state !== "ready") {
      this.queue = [];
      return;
    }
    
    this.isProcessing = true;
    const { text, onEnd } = this.queue.shift()!;
    
    const cleanText = this.cleanText(text);
    if (!cleanText) {
      this.isProcessing = false;
      onEnd?.();
      this.processQueue();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(cleanText);
    const voice = this.selectedVoice || this.getPreferredVoice();
    if (voice) utterance.voice = voice;
    
    utterance.rate = this.speechRate;
    utterance.pitch = 1.05;
    utterance.volume = 1;
    
    utterance.onend = () => {
      this.isProcessing = false;
      this.currentUtterance = null;
      onEnd?.();
      this.processQueue();
    };
    
    utterance.onerror = (event) => {
      console.error("[VoiceController] Utterance error:", event.error);
      this.isProcessing = false;
      this.currentUtterance = null;
      onEnd?.();
      this.processQueue();
    };
    
    this.currentUtterance = utterance;
    window.speechSynthesis.speak(utterance);
  }
  
  speak(text: string, onEnd?: () => void): boolean {
    if (!this.isSupported() || this.state !== "ready") {
      onEnd?.();
      return false;
    }
    
    this.queue = [];
    window.speechSynthesis.cancel();
    this.isProcessing = false;
    
    this.queue.push({ text, onEnd });
    this.processQueue();
    return true;
  }
  
  queueSpeak(text: string, onEnd?: () => void): boolean {
    if (!this.isSupported() || this.state !== "ready") {
      onEnd?.();
      return false;
    }
    
    this.queue.push({ text, onEnd });
    this.processQueue();
    return true;
  }

  private streamBuffer = "";
  private streamActive = false;
  private streamOnEnd: (() => void) | null = null;

  startStreamSpeak(): void {
    if (!this.isSupported() || this.state !== "ready") return;
    this.stop();
    this.streamBuffer = "";
    this.streamActive = true;
    this.streamOnEnd = null;
  }

  feedStreamChunk(chunk: string): void {
    if (!this.streamActive) return;
    this.streamBuffer += chunk;

    const sentenceBreaks = /([.!?;:]\s)|(\n)/;
    let match: RegExpExecArray | null;
    while ((match = sentenceBreaks.exec(this.streamBuffer)) !== null) {
      const idx = match.index + match[0].length;
      const sentence = this.streamBuffer.slice(0, idx).trim();
      this.streamBuffer = this.streamBuffer.slice(idx);
      if (sentence.length > 2) {
        this.queueSpeak(sentence);
      }
    }
  }

  endStreamSpeak(onEnd?: () => void): void {
    if (!this.streamActive) {
      onEnd?.();
      return;
    }
    this.streamActive = false;
    const remaining = this.streamBuffer.trim();
    this.streamBuffer = "";
    if (remaining.length > 2) {
      this.queueSpeak(remaining, onEnd);
    } else {
      if (this.isProcessing || this.queue.length > 0) {
        this.streamOnEnd = onEnd ?? null;
        const origProcessQueue = this.processQueue.bind(this);
        const self = this;
        const waitForDone = () => {
          if (self.queue.length === 0 && !self.isProcessing) {
            self.streamOnEnd?.();
            self.streamOnEnd = null;
          } else {
            setTimeout(waitForDone, 100);
          }
        };
        waitForDone();
      } else {
        onEnd?.();
      }
    }
  }

  isStreamActive(): boolean {
    return this.streamActive;
  }
  
  setRate(rate: number): void {
    this.speechRate = Math.max(0.5, Math.min(2.0, rate));
    try { localStorage.setItem("memnora-narration-speed", String(this.speechRate)); } catch (_) {}
  }

  getRate(): number {
    return this.speechRate;
  }

  stop(): void {
    this.queue = [];
    this.streamBuffer = "";
    this.streamActive = false;
    this.streamOnEnd = null;
    if (this.isSupported()) {
      window.speechSynthesis.cancel();
    }
    this.isProcessing = false;
    this.currentUtterance = null;
  }
  
  isSpeaking(): boolean {
    if (!this.isSupported()) return false;
    return window.speechSynthesis.speaking;
  }
}

export const voiceController = VoiceController.getInstance();
export default VoiceController;
